export const RENEWAL_WARNING_DAYS = 45;

export const DEFAULT_FILTERS = {
  search: "",
  status: "",
  payment: "",
  formule: "",
  sort: "expiryAsc",
  perPage: 25,
};

export const RENEWAL_STATUS_OPTIONS = [
  { value: "", label: "Tous les statuts" },
  { value: "active", label: "Actifs" },
  { value: "dueSoon", label: "À renouveler bientôt" },
  { value: "expired", label: "Expirés" },
  { value: "unpaid", label: "Non réglés" },
];

export const PAYMENT_STATUS_OPTIONS = [
  { value: "", label: "Tous les paiements" },
  { value: "paid", label: "Réglés" },
  { value: "unpaid", label: "Non réglés" },
];

export function normalizeText(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

export function formatNumber(value) {
  return new Intl.NumberFormat("fr-FR").format(Number(value || 0));
}

export function formatCurrency(value) {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
  }).format(Number(value || 0));
}

export function extractSubscriptions(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.achats)) return data.achats;
  if (Array.isArray(data?.reglements)) return data.reglements;
  if (Array.isArray(data?.data)) return data.data;

  return [];
}

export function toDate(value) {
  if (!value) return null;

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return null;

  return date;
}

export function startOfDay(date) {
  const safeDate = toDate(date) || new Date();

  return new Date(
    safeDate.getFullYear(),
    safeDate.getMonth(),
    safeDate.getDate(),
  );
}

export function addOneYear(value) {
  const date = toDate(value);

  if (!date) return null;

  const expiryDate = new Date(date);
  expiryDate.setFullYear(expiryDate.getFullYear() + 1);

  return expiryDate;
}

export function getDaysUntil(value) {
  const date = toDate(value);

  if (!date) return null;

  const today = startOfDay(new Date());
  const target = startOfDay(date);

  return Math.ceil((target.getTime() - today.getTime()) / 86400000);
}

export function formatDateFr(value) {
  const date = toDate(value);

  if (!date) return "Date non renseignée";

  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
}

export function formatLongDateFr(value) {
  const date = toDate(value);

  if (!date) return "Date non renseignée";

  return new Intl.DateTimeFormat("fr-FR", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  }).format(date);
}

export function getSubscriptionUser(subscription) {
  return (
    subscription?.User ||
    subscription?.Users ||
    subscription?.user ||
    subscription?.Utilisateur ||
    null
  );
}

export function getUserName(user) {
  return (
    [user?.prenom, user?.nom].filter(Boolean).join(" ") ||
    [user?.first_name, user?.last_name].filter(Boolean).join(" ") ||
    user?.name ||
    user?.username ||
    "Utilisateur non renseigné"
  );
}

export function getUserEmail(user) {
  return user?.email || user?.mail || "Email non renseigné";
}

export function getSubscriptionUserName(subscription) {
  return getUserName(getSubscriptionUser(subscription));
}

export function getSubscriptionUserEmail(subscription) {
  return getUserEmail(getSubscriptionUser(subscription));
}

export function getSubscriptionFormula(subscription) {
  return (
    subscription?.formule ||
    subscription?.type_achat ||
    subscription?.message_achat ||
    "Formule non renseignée"
  );
}

export function getSubscriptionAmount(subscription) {
  return Number(subscription?.prix_total || 0);
}

export function getSubscriptionPurchaseDate(subscription) {
  return subscription?.date_achat || subscription?.created_at || subscription?.createdAt || null;
}

export function getSubscriptionExpiryDate(subscription) {
  return addOneYear(getSubscriptionPurchaseDate(subscription));
}

export function isPaymentPaid(subscription) {
  const statut = normalizeText(subscription?.statut_reglement);
  const stripeStatus = normalizeText(subscription?.payment_status);

  return (
    statut === "regle" ||
    statut === "réglé" ||
    statut === "paye" ||
    statut === "payé" ||
    stripeStatus === "paid" ||
    stripeStatus === "succeeded" ||
    stripeStatus === "complete"
  );
}

export function getPaymentLabel(subscription) {
  if (isPaymentPaid(subscription)) return "Réglé";

  return subscription?.statut_reglement || subscription?.payment_status || "Non réglé";
}

export function getPaymentStyle(subscription) {
  if (isPaymentPaid(subscription)) {
    return "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-200";
  }

  return "bg-red-100 text-red-700 dark:bg-red-500/10 dark:text-red-200";
}

export function getRenewalStatus(subscription) {
  if (!isPaymentPaid(subscription)) return "unpaid";

  const expiryDate = getSubscriptionExpiryDate(subscription);
  const daysUntil = getDaysUntil(expiryDate);

  if (daysUntil === null) return "expired";
  if (daysUntil < 0) return "expired";
  if (daysUntil <= RENEWAL_WARNING_DAYS) return "dueSoon";

  return "active";
}

export function getRenewalStatusLabel(subscription) {
  const status =
    typeof subscription === "string" ? subscription : getRenewalStatus(subscription);

  if (status === "active") return "Actif";
  if (status === "dueSoon") return "À renouveler";
  if (status === "expired") return "Expiré";
  if (status === "unpaid") return "Non réglé";

  return "Inconnu";
}

export function getRenewalStatusStyle(subscription) {
  const status =
    typeof subscription === "string" ? subscription : getRenewalStatus(subscription);

  if (status === "active") {
    return "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-200";
  }

  if (status === "dueSoon") {
    return "bg-amber-100 text-amber-700 dark:bg-amber-500/10 dark:text-amber-200";
  }

  if (status === "expired") {
    return "bg-red-100 text-red-700 dark:bg-red-500/10 dark:text-red-200";
  }

  if (status === "unpaid") {
    return "bg-slate-100 text-slate-700 dark:bg-white/10 dark:text-white/70";
  }

  return "bg-slate-100 text-slate-700 dark:bg-white/10 dark:text-white/70";
}

export function getRenewalMessage(subscription) {
  const status = getRenewalStatus(subscription);
  const expiryDate = getSubscriptionExpiryDate(subscription);
  const daysUntil = getDaysUntil(expiryDate);

  if (status === "unpaid") return "Paiement non réglé.";
  if (status === "expired") {
    return `Expiré depuis ${Math.abs(daysUntil || 0)} jour${
      Math.abs(daysUntil || 0) > 1 ? "s" : ""
    }.`;
  }
  if (status === "dueSoon") {
    return `À renouveler dans ${daysUntil} jour${daysUntil > 1 ? "s" : ""}.`;
  }

  return `Valable encore ${daysUntil} jour${daysUntil > 1 ? "s" : ""}.`;
}

export function getSubscriptionGradient(subscription) {
  const status = getRenewalStatus(subscription);

  if (status === "active") return "from-emerald-600 to-teal-700";
  if (status === "dueSoon") return "from-amber-500 to-orange-600";
  if (status === "expired") return "from-red-600 to-rose-700";

  return "from-slate-700 to-slate-950";
}

export function getUniqueFormules(subscriptions = []) {
  const formules = subscriptions
    .map(getSubscriptionFormula)
    .filter(Boolean)
    .filter((formule) => formule !== "Formule non renseignée");

  return Array.from(new Set(formules)).sort((a, b) => a.localeCompare(b, "fr"));
}

export function filterSubscriptions(subscriptions = [], filters = DEFAULT_FILTERS) {
  const search = normalizeText(filters.search);
  const status = String(filters.status || "");
  const payment = String(filters.payment || "");
  const formule = normalizeText(filters.formule);

  return subscriptions.filter((subscription) => {
    const renewalStatus = getRenewalStatus(subscription);
    const paid = isPaymentPaid(subscription);

    const haystack = normalizeText(
      [
        getSubscriptionUserName(subscription),
        getSubscriptionUserEmail(subscription),
        getSubscriptionFormula(subscription),
        subscription?.statut_reglement,
        subscription?.payment_status,
      ].join(" "),
    );

    const matchesSearch = !search || haystack.includes(search);
    const matchesStatus = !status || renewalStatus === status;

    const matchesPayment =
      !payment ||
      (payment === "paid" && paid) ||
      (payment === "unpaid" && !paid);

    const matchesFormule =
      !formule || normalizeText(getSubscriptionFormula(subscription)) === formule;

    return matchesSearch && matchesStatus && matchesPayment && matchesFormule;
  });
}

export function sortSubscriptions(subscriptions = [], sort = "expiryAsc") {
  return [...subscriptions].sort((a, b) => {
    const nameA = normalizeText(getSubscriptionUserName(a));
    const nameB = normalizeText(getSubscriptionUserName(b));

    const amountA = getSubscriptionAmount(a);
    const amountB = getSubscriptionAmount(b);

    const purchaseA = toDate(getSubscriptionPurchaseDate(a))?.getTime() || 0;
    const purchaseB = toDate(getSubscriptionPurchaseDate(b))?.getTime() || 0;

    const expiryA = getSubscriptionExpiryDate(a)?.getTime() || 0;
    const expiryB = getSubscriptionExpiryDate(b)?.getTime() || 0;

    const idA = Number(a?.id || 0);
    const idB = Number(b?.id || 0);

    if (sort === "expiryDesc") return expiryB - expiryA;
    if (sort === "purchaseDesc") return purchaseB - purchaseA;
    if (sort === "purchaseAsc") return purchaseA - purchaseB;
    if (sort === "amountDesc") return amountB - amountA;
    if (sort === "amountAsc") return amountA - amountB;
    if (sort === "userAsc") return nameA.localeCompare(nameB, "fr");
    if (sort === "userDesc") return nameB.localeCompare(nameA, "fr");
    if (sort === "recent") return idB - idA;
    if (sort === "oldest") return idA - idB;

    return expiryA - expiryB;
  });
}

export function paginateItems(items = [], page = 1, perPage = 25) {
  const safePerPage = Number(perPage || 25);
  const totalPages = Math.max(Math.ceil(items.length / safePerPage), 1);
  const safePage = Math.min(Math.max(Number(page || 1), 1), totalPages);
  const start = (safePage - 1) * safePerPage;

  return {
    page: safePage,
    totalPages,
    items: items.slice(start, start + safePerPage),
  };
}

export function buildSubscriptionStats(subscriptions = []) {
  let active = 0;
  let dueSoon = 0;
  let expired = 0;
  let unpaid = 0;
  let paid = 0;
  let revenue = 0;

  subscriptions.forEach((subscription) => {
    const status = getRenewalStatus(subscription);

    if (status === "active") active += 1;
    else if (status === "dueSoon") dueSoon += 1;
    else if (status === "expired") expired += 1;
    else if (status === "unpaid") unpaid += 1;

    if (isPaymentPaid(subscription)) {
      paid += 1;
      revenue += getSubscriptionAmount(subscription);
    }
  });

  return {
    total: subscriptions.length,
    active,
    dueSoon,
    expired,
    unpaid,
    paid,
    revenue,
    alerts: dueSoon + expired + unpaid,
  };
}